// Period title + popover picker. Clicking the title opens a selector shaped
// by the active view mode: Year → year grid, Month → 12-month grid with a
// year stepper, Week/Day → a mini calendar (weeks start Monday, like the
// period model). Choices outside [min, max-or-now] are disabled — the same
// occupancy bounds the prev/next stepper enforces.

import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import {
  endOfPeriod,
  MONTH_LONG,
  MONTH_SHORT,
  periodTitle,
  startOfPeriod,
  WEEKDAY_SHORT,
  type ViewMode,
} from '../lib/meterCharts';
import Icon from './Icon';
import { useDismiss } from './ui';

// Monday-first weekday header (WEEKDAY_SHORT is Sun-indexed).
const MON_FIRST = [1, 2, 3, 4, 5, 6, 0];

export default function PeriodPicker({
  view,
  anchor,
  min,
  max,
  now = new Date(),
  onChange,
}: {
  view: ViewMode;
  anchor: Date;
  min?: Date | null;
  max?: Date | null;
  /** "Now" as a wall date on the project calendar (lib/zone.ts). */
  now?: Date;
  onChange: (d: Date) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  // The year/month the popover is showing (navigation state, not selection).
  const [dispYear, setDispYear] = useState(0);
  const [dispMonth, setDispMonth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, () => setOpen(false));

  const maxEff = max && max < now ? max : now;

  // A candidate period [start, end) is selectable iff it overlaps the window.
  const selectable = (start: Date, end: Date) => (!min || end > min) && start <= maxEff;

  const toggle = () => {
    if (!open) {
      setDispYear(anchor.getUTCFullYear());
      setDispMonth(anchor.getUTCMonth());
    }
    setOpen((o) => !o);
  };

  const pick = (d: Date) => {
    onChange(d);
    setOpen(false);
  };

  const selStart = startOfPeriod(anchor, view).getTime();

  let body: ReactElement;
  if (view === 'year') {
    const yMax = maxEff.getUTCFullYear();
    const yMin = min ? min.getUTCFullYear() : yMax - 11;
    const years = Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i);
    body = (
      <div className="picker-grid cols-4">
        {years.map((y) => {
          const start = new Date(Date.UTC(y, 0, 1));
          return (
            <button
              key={y}
              className={`picker-cell${start.getTime() === selStart ? ' selected' : ''}`}
              disabled={!selectable(start, new Date(Date.UTC(y + 1, 0, 1)))}
              onClick={() => pick(start)}
            >
              {y}
            </button>
          );
        })}
      </div>
    );
  } else if (view === 'month') {
    const yMax = maxEff.getUTCFullYear();
    const yMin = min ? min.getUTCFullYear() : yMax - 11;
    body = (
      <>
        <div className="picker-head">
          <button
            className="icon-btn"
            disabled={dispYear <= yMin}
            onClick={() => setDispYear((y) => y - 1)}
            aria-label="Previous year"
          >
            <Icon name="chevron-left" size={17} />
          </button>
          <span>{dispYear}</span>
          <button
            className="icon-btn"
            disabled={dispYear >= yMax}
            onClick={() => setDispYear((y) => y + 1)}
            aria-label="Next year"
          >
            <Icon name="chevron-right" size={17} />
          </button>
        </div>
        <div className="picker-grid cols-3">
          {MONTH_SHORT.map((name, m) => {
            const start = new Date(Date.UTC(dispYear, m, 1));
            return (
              <button
                key={name}
                className={`picker-cell${start.getTime() === selStart ? ' selected' : ''}`}
                disabled={!selectable(start, new Date(Date.UTC(dispYear, m + 1, 1)))}
                onClick={() => pick(start)}
              >
                {name}
              </button>
            );
          })}
        </div>
      </>
    );
  } else {
    // day / week — mini calendar of dispYear/dispMonth.
    const first = new Date(Date.UTC(dispYear, dispMonth, 1));
    const gridStart = startOfPeriod(first, 'week');
    const cells = Array.from(
      { length: 42 },
      (_, i) =>
        new Date(Date.UTC(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + i)),
    );
    const selEnd = endOfPeriod(startOfPeriod(anchor, view), view).getTime();
    const prevMonth = new Date(Date.UTC(dispYear, dispMonth - 1, 1));
    const nextMonth = new Date(Date.UTC(dispYear, dispMonth + 1, 1));
    body = (
      <>
        <div className="picker-head">
          <button
            className="icon-btn"
            disabled={!selectable(prevMonth, new Date(Date.UTC(dispYear, dispMonth, 1)))}
            onClick={() => {
              setDispYear(prevMonth.getUTCFullYear());
              setDispMonth(prevMonth.getUTCMonth());
            }}
            aria-label="Previous month"
          >
            <Icon name="chevron-left" size={17} />
          </button>
          <span>
            {MONTH_LONG[dispMonth]} {dispYear}
          </span>
          <button
            className="icon-btn"
            disabled={!selectable(nextMonth, new Date(Date.UTC(dispYear, dispMonth + 2, 1)))}
            onClick={() => {
              setDispYear(nextMonth.getUTCFullYear());
              setDispMonth(nextMonth.getUTCMonth());
            }}
            aria-label="Next month"
          >
            <Icon name="chevron-right" size={17} />
          </button>
        </div>
        <div className="picker-grid cols-7 weekdays">
          {MON_FIRST.map((d) => (
            <span key={d}>{WEEKDAY_SHORT[d]}</span>
          ))}
        </div>
        <div className="picker-grid cols-7">
          {cells.map((d) => {
            const t = d.getTime();
            const inSel = t >= selStart && t < selEnd;
            return (
              <button
                key={t}
                className={`picker-cell day${inSel ? ' selected' : ''}${
                  d.getUTCMonth() !== dispMonth ? ' faded' : ''
                }`}
                disabled={
                  !selectable(
                    startOfPeriod(d, view),
                    endOfPeriod(startOfPeriod(d, view), view),
                  )
                }
                onClick={() => pick(d)}
              >
                {d.getUTCDate()}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div className="period-pick" ref={ref}>
      <button className="period-title" onClick={toggle} aria-expanded={open}>
        {periodTitle(anchor, view)}
      </button>
      {open && <div className="picker-pop">{body}</div>}
    </div>
  );
}
